import { useState } from 'react';
import { BarChart, Check, ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import {
  AutoReviewAction,
  AutoReviewCandidate,
  AutoReviewDecision,
  AutoReviewGrade,
  AutoReviewRunner,
  useMeetingAutoReview,
  useRunAutoReview,
} from '../../../queries/autoReview';
import { useActiveAnalysisModels } from '../../../queries/analysisModels';
import { useToast } from '../../../contexts/ToastContext';
import { useN8nWebhook } from '../../../hooks/useN8nWebhook';
import { resolveN8nBase } from '../../../components/ModelRunControl';
import { ModelRerun, planReruns, rerunUrls } from '../../../lib/auto-review-rerun';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Badge, BadgeProps } from '../../../components/ui/Badge';
import { Spinner } from '../../../components/ui/Spinner';
import { cn } from '../../../lib/utils';

const GRADE: Record<AutoReviewGrade, { variant: BadgeProps['variant']; title: string }> = {
  A: { variant: 'success', title: 'Release with confidence' },
  B: { variant: 'primary', title: 'Release' },
  C: { variant: 'warning', title: 'Liveable - prefer the majority horse' },
  D: { variant: 'destructive', title: 'No majority - would re-run' },
};

export const ACTION_TEXT: Record<AutoReviewAction, string> = {
  release: 'Release',
  release_majority: 'Release the majority horse (slot 1 outvoted)',
  rerun: 'Would re-run (no majority)',
};

type Runners = Record<string, AutoReviewRunner>;

const num = (v: number | string | null | undefined): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** "#3 Fast One", falling back to whatever is known about the runner. */
function horse(runners: Runners, runnerId: string | null | undefined): string {
  if (!runnerId) return '–';
  const r = runners[runnerId];
  if (!r) return runnerId;
  const tab = r.tab_number != null ? `#${r.tab_number}` : '';
  return [tab, r.horse_name].filter(Boolean).join(' ') || runnerId;
}

function tab(runners: Runners, runnerId: string): string {
  const t = runners[runnerId]?.tab_number;
  return t != null ? `#${t}` : (runners[runnerId]?.horse_name ?? runnerId);
}

const price = (odds: number | string | null) => {
  const n = num(odds);
  return n == null ? '' : ` $${n.toFixed(2)}`;
};

/** Backup tips: "#3 $4.20, #7 $6.00, value #9 $12.00" - everything but the top pick. */
export function backupTipsText(c: AutoReviewCandidate, runners: Runners): string {
  const backups = (c.tips ?? []).filter((t) => t.pick !== '1');
  if (backups.length === 0) return '–';
  return backups
    .map((t) => `${t.pick === 'value' ? 'value ' : ''}${tab(runners, t.runner_id)}${price(t.odds)}`)
    .join(', ');
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

function CandidatesTable({ decision, runners }: { decision: AutoReviewDecision; runners: Runners }) {
  return (
    <table className="mt-2 w-full text-xs">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-1 pr-3 font-medium">Slot</th>
          <th className="py-1 pr-3 font-medium">Model</th>
          <th className="py-1 pr-3 font-medium">Top pick</th>
          <th className="py-1 pr-3 font-medium">Backup tips</th>
          <th className="py-1 pr-3 text-right font-medium">Backup chance</th>
          <th className="py-1 pr-3 text-right font-medium">Calibration</th>
          <th className="py-1 text-right font-medium">Score</th>
        </tr>
      </thead>
      <tbody>
        {decision.candidates.map((c) => {
          const chosen = c.slot === decision.chosen_slot;
          const chance = num(c.backup_chance);
          const calibration = num(c.calibration);
          const score = num(c.score);
          return (
            <tr
              key={c.slot}
              data-testid={`candidate-slot-${c.slot}`}
              data-chosen={chosen ? 'true' : undefined}
              className={cn('border-t border-border/50', chosen && 'bg-primary/5 font-medium')}
            >
              <td className="py-1 pr-3 tabular-nums">
                {c.slot}
                {chosen && (
                  <Badge variant="primary" className="ml-1.5">
                    Chosen
                  </Badge>
                )}
              </td>
              <td className="py-1 pr-3">{c.model ?? '–'}</td>
              <td className="py-1 pr-3">
                <span
                  className={cn(c.in_majority && 'rounded bg-emerald-500/12 px-1 text-emerald-700 dark:text-emerald-400')}
                  title={c.in_majority ? 'In the majority' : undefined}
                  data-majority={c.in_majority ? 'true' : undefined}
                >
                  {horse(runners, c.top1)}
                </span>
              </td>
              <td className="py-1 pr-3 tabular-nums">{backupTipsText(c, runners)}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{chance == null ? '–' : `${(chance * 100).toFixed(1)}%`}</td>
              <td className="py-1 pr-3 text-right tabular-nums">{calibration == null ? '–' : `${calibration.toFixed(2)}×`}</td>
              <td className="py-1 text-right tabular-nums">{score == null ? '–' : score.toFixed(3)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function DecisionRow({ decision, runners }: { decision: AutoReviewDecision; runners: Runners }) {
  const [open, setOpen] = useState(false);
  const grade = GRADE[decision.grade];
  const differs = decision.suggested_runner_id !== decision.slot1_runner_id;
  const raceLabel = decision.race_number != null ? `R${decision.race_number}` : decision.race_id;

  return (
    <div className="px-4 py-3" data-testid={`auto-review-${decision.race_id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="text-sm font-semibold tabular-nums">{raceLabel}</span>
        <Badge variant={grade?.variant ?? 'default'} title={grade?.title} aria-label={`Grade ${decision.grade}`}>
          {decision.grade}
        </Badge>
        <span className="text-xs tabular-nums text-muted-foreground" title="Agreement pattern across the model slots">
          {decision.pattern}
        </span>
        <span className="text-sm">{ACTION_TEXT[decision.action] ?? decision.action}</span>
      </div>

      <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm">
        <span>
          <span className="text-muted-foreground">Suggested: </span>
          {horse(runners, decision.suggested_runner_id)}
        </span>
        <span>
          <span className="text-muted-foreground">Slot 1: </span>
          {horse(runners, decision.slot1_runner_id)}
        </span>
        {differs ? (
          <Badge variant="warning">Differs from slot 1</Badge>
        ) : (
          <Badge variant="outline">Same as slot 1</Badge>
        )}
        {decision.chosen_model && (
          <span>
            <span className="text-muted-foreground">Model put forward: </span>
            {decision.chosen_model}
            {decision.chosen_slot != null && <span className="text-muted-foreground"> (slot {decision.chosen_slot})</span>}
          </span>
        )}
      </div>

      <p className="mt-1.5 text-sm text-muted-foreground">{decision.reason}</p>

      {decision.candidates.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Candidates ({decision.candidates.length})
          </button>
          {open && <CandidatesTable decision={decision} runners={runners} />}
        </>
      )}
    </div>
  );
}

const raceList = (r: ModelRerun) => r.races.map((x) => `R${x.raceNumber} (slot ${x.slot})`).join(', ');

/**
 * One button per model that ran on the meeting's C and D races: pressing it re-runs that model on each of
 * those races, into the slot it holds there. Runs are fire-and-forget (a robot's webhook answers only when it
 * finishes), so a pressed button shows "Started" until the review changes. The scheduled review picks up the
 * new tips once the robots go quiet, or staff press "Run auto review now".
 */
function RerunWeakRaces({
  decisions,
  trackId,
  meetingDate,
  reviewKey,
}: {
  decisions: AutoReviewDecision[];
  trackId: string;
  meetingDate: string;
  reviewKey: string | null;
}) {
  const { data: models = [] } = useActiveAnalysisModels();
  const { triggerWebhook } = useN8nWebhook();
  const { success, error } = useToast();
  // models started since this review; a new review (reviewKey changes) re-arms every button
  const [started, setStarted] = useState<{ key: string | null; models: string[] }>({ key: reviewKey, models: [] });
  const startedModels = started.key === reviewKey ? started.models : [];

  const reruns = planReruns(decisions, models);
  if (reruns.length === 0) return null;
  const weakRaces = new Set(reruns.flatMap((r) => r.races.map((x) => x.raceNumber))).size;

  const run = (rerun: ModelRerun) => {
    const base = resolveN8nBase();
    if (!base) {
      error('n8n server URL is not configured (VITE_N8N_URL).');
      return;
    }
    const urls = rerunUrls(base, rerun, { trackId, meetingDate });
    if (urls.length === 0) return;
    for (const url of urls) void triggerWebhook(url, `${rerun.registry!.label} re-run`);
    setStarted({ key: reviewKey, models: [...startedModels, rerun.model] });
    success(`Started ${rerun.registry!.label} on ${raceList(rerun)}.`);
  };

  return (
    <Card className="p-4" data-testid="auto-review-rerun">
      <p className="text-sm">
        <span className="font-medium">Re-run C and D races</span>
        <span className="text-muted-foreground">
          {' '}
          ({weakRaces} race{weakRaces === 1 ? '' : 's'}) - each model re-runs into the slot it holds in that race.
        </span>
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {reruns.map((r) => {
          const done = startedModels.includes(r.model);
          return (
            <Button
              key={r.model}
              size="sm"
              variant="outline"
              disabled={!r.registry || done}
              onClick={() => run(r)}
              title={
                r.registry
                  ? `${r.registry.label}: ${raceList(r)}`
                  : `No active model in the registry matches "${r.model}", so it cannot be run from here.`
              }
            >
              {done ? <Check className="h-3.5 w-3.5" /> : <BarChart className="h-3.5 w-3.5" />}
              {r.registry?.label ?? r.model} ({r.races.length})
              {done && ' - started'}
            </Button>
          );
        })}
      </div>
    </Card>
  );
}

/** The auto review's suggestions for a meeting, one row per race. */
export function AutoReviewPanel({
  meetingId,
  trackId,
  meetingDate,
}: {
  meetingId: string;
  /** with meetingDate, enables the C/D re-run buttons */
  trackId?: string;
  meetingDate?: string;
}) {
  const { data, isLoading, isError } = useMeetingAutoReview(meetingId);
  const run = useRunAutoReview(meetingId);
  const { success, error } = useToast();

  const runNow = () =>
    run.mutate(undefined, {
      onSuccess: ({ racesChanged }) =>
        success(
          racesChanged > 0
            ? `Auto review updated: ${racesChanged} race${racesChanged === 1 ? '' : 's'} changed.`
            : 'Auto review is up to date: no races changed.',
        ),
      onError: () => error('Failed to run the auto review. Please try again.'),
    });

  if (isLoading) return <Spinner label="Loading auto review…" />;

  const empty = (text: string) => (
    <div className="rounded-xl border border-dashed border-border py-12 text-center text-muted-foreground">{text}</div>
  );

  if (isError || !data) return empty('Failed to load the auto review.');
  if (!data.available) return empty('Auto review is not available on this database yet.');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground" data-testid="auto-review-header">
          Suggest mode - slot 5 holds these suggestions; slots 1-4 are unchanged.
          {data.reviewedAt && ` Last reviewed ${formatTime(data.reviewedAt)}.`}
        </p>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          onClick={runNow}
          disabled={run.isPending}
          title="Review this meeting now, e.g. after re-running races (the schedule runs every 15 minutes and skips training meetings)"
        >
          {run.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Run auto review now
        </Button>
      </div>
      {trackId && meetingDate && (
        <RerunWeakRaces decisions={data.decisions} trackId={trackId} meetingDate={meetingDate} reviewKey={data.reviewedAt} />
      )}
      {data.decisions.length === 0 ? (
        empty('Not reviewed yet.')
      ) : (
        <Card className="divide-y divide-border overflow-hidden">
          {data.decisions.map((d) => (
            <DecisionRow key={d.race_id} decision={d} runners={data.runners ?? {}} />
          ))}
        </Card>
      )}
    </div>
  );
}
